/*!
 * Copyright 2019 Fondazione Bruno Kessler
 * Licensed under the EUPL, Version 1.1 or - as soon they will be approved by the European
 * Commission - subsequent versions of the EUPL (the "Licence"); You may not use this work
 * except in compliance with the Licence.
 *
 * You may obtain a copy of the Licence at:
 * http://ec.europa.eu/idabc/eupl.html
 *
 * Unless required by applicable law or agreed to in writing, software distributed under
 * the Licence is distributed on an "AS IS" basis, WITHOUT WARRANTIES OR CONDITIONS OF
 * ANY KIND, either express or implied. See the Licence for the specific language
 * governing permissions and limitations under the Licence.
 */

import { Injectable } from "@angular/core";
import {
  ActivatedRouteSnapshot,
  CanActivate,
  Router,
  RouterStateSnapshot,
  UrlTree,
} from "@angular/router";
import { CommonState } from "@app/store/common/common.state";
import { Store } from "@ngxs/store";
import { Observable } from "rxjs";
import { filter, map, take } from "rxjs/operators";

/**
 * Check if a certain TreC feature is available, if not the user will be redirected to homepage.
 *
 * e.g. if video-chat feature is not available in the current environemnt, when the user tries to go to video-meeting calendar page, he/she
 * will be redirected to the homepage.
 *
 * The name of the feature/service to check can be passed using the key {@link IsFeatureAvailableGuard.DATA_REQUIRED_FEATURE} in the
 * router's data object.
 */
@Injectable({
  providedIn: "root",
})
export class IsFeatureAvailableGuard implements CanActivate {
  static readonly DATA_REQUIRED_FEATURE = "requiredFeature";

  constructor(private router: Router, private store: Store) {}

  canActivate(
    next: ActivatedRouteSnapshot,
    _state: RouterStateSnapshot
  ): Observable<UrlTree | boolean> {
    const service: string = next.data[IsFeatureAvailableGuard.DATA_REQUIRED_FEATURE];

    return this.store.select(CommonState.isFeatureAvailable(service)).pipe(
      filter((isAvailable) => isAvailable !== undefined), // Wait until a value has been set (info has been fetched)
      take(1), // Pick first
      map(
        (isAvailable) =>
          isAvailable
            ? true // Continue navigation
            : this.router.createUrlTree(["/"]) // Redirect to homepage
      )
    );
  }
}
